import React, { useEffect, useState } from 'react';
import { roomTypesApi } from '../services/api';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { roomsApi } from '../services/api';
import RoomDetailDialog from '../components/RoomDetailDialog';

interface Room {
  id: number;
  room_number: string;
  floor?: number;
  status: 'available' | 'occupied' | 'reserved' | 'maintenance' | 'out_of_order';
  room_type?: {
    id: number;
    name: string;
    base_rate: number;
  };
}

const Rooms: React.FC = () => {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [roomTypes, setRoomTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRoom, setEditingRoom] = useState<Room | null>(null);
  const [selectedRoomId, setSelectedRoomId] = useState<number | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  useEffect(() => {
    fetchRooms();
    fetchRoomTypes();
  }, []);

  const fetchRoomTypes = async () => {
    try {
      const response = await roomTypesApi.getAll();
      setRoomTypes(response.data.data || []);
    } catch (err) {
      console.error('Failed to load room types:', err);
    }
  };

  const fetchRooms = async () => {
    try {
      setLoading(true);
      const response = await roomsApi.getAll();
      setRooms(response.data.data || []);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load rooms');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: 'success' | 'warning' | 'error' | 'info' | 'default' } = {
      available: 'success',
      occupied: 'error',
      reserved: 'warning',
      maintenance: 'info',
      out_of_order: 'default',
    };
    return colors[status] || 'default';
  };

  const handleEdit = (room: Room) => {
    setEditingRoom(room);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRoom(null);
  };

  const handleSave = async () => {
    try {
      const formData = new FormData(document.getElementById('room-form') as HTMLFormElement);
      const roomNumber = formData.get('room_number') as string;
      const floorValue = formData.get('floor') as string;
      const status = (formData.get('status') as string) || 'available';
      const roomTypeIdValue = formData.get('room_type_id') as string;

      if (!roomNumber || !roomNumber.trim()) {
        setError('Room number is required');
        return;
      }

      const roomData: any = {
        room_number: roomNumber.trim(),
        status: status,
      };

      if (floorValue && floorValue.trim()) {
        roomData.floor = parseInt(floorValue);
      }

      if (roomTypeIdValue && roomTypeIdValue.trim() && roomTypeIdValue !== 'null') {
        roomData.room_type_id = parseInt(roomTypeIdValue);
      }

      if (editingRoom) {
        await roomsApi.update(editingRoom.id, roomData);
      } else {
        await roomsApi.create(roomData);
      }
      handleCloseDialog();
      fetchRooms();
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || 'Failed to save room';
      setError(errorMsg);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const stats = {
    total: rooms.length,
    available: rooms.filter(r => r.status === 'available').length,
    occupied: rooms.filter(r => r.status === 'occupied').length,
    reserved: rooms.filter(r => r.status === 'reserved').length,
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Rooms Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Add Room
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Rooms</Typography>
              <Typography variant="h4">{stats.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Available</Typography>
              <Typography variant="h4" color="success.main">{stats.available}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Occupied</Typography>
              <Typography variant="h4" color="error.main">{stats.occupied}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Reserved</Typography>
              <Typography variant="h4" color="warning.main">{stats.reserved}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Room Number</TableCell>
              <TableCell>Floor</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Rate</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rooms.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="textSecondary" sx={{ py: 3 }}>
                    No rooms found. Add your first room to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rooms.map((room) => (
                <TableRow key={room.id} hover>
                  <TableCell>{room.room_number}</TableCell>
                  <TableCell>{room.floor || '-'}</TableCell>
                  <TableCell>{room.room_type?.name || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={room.status}
                      color={getStatusColor(room.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {room.room_type?.base_rate ? `$${room.room_type.base_rate}` : '-'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedRoomId(room.id);
                        setDetailDialogOpen(true);
                      }}
                      title="View Details"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={async () => {
                        if (window.confirm('Are you sure you want to delete this room?')) {
                          try {
                            await roomsApi.delete(room.id);
                            fetchRooms();
                          } catch (err: any) {
                            setError(err.response?.data?.message || 'Failed to delete room');
                          }
                        }
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingRoom ? 'Edit Room' : 'Add New Room'}</DialogTitle>
        <form id="room-form" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
          <DialogContent>
            <TextField
              fullWidth
              name="room_number"
              label="Room Number"
              margin="normal"
              defaultValue={editingRoom?.room_number || ''}
              required
              inputProps={{ maxLength: 20 }}
            />
            <TextField
              fullWidth
              name="room_type_id"
              select
              label="Room Type"
              margin="normal"
              defaultValue={editingRoom?.room_type?.id || ''}
            >
              <MenuItem value="">None</MenuItem>
              {roomTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.name} - ${type.base_rate}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              name="floor"
              label="Floor"
              type="number"
              margin="normal"
              defaultValue={editingRoom?.floor || ''}
              inputProps={{ min: 0 }}
            />
            <TextField
              fullWidth
              name="status"
              select
              label="Status"
              margin="normal"
              defaultValue={editingRoom?.status || 'available'}
              required
            >
              <MenuItem value="available">Available</MenuItem>
              <MenuItem value="occupied">Occupied</MenuItem>
              <MenuItem value="reserved">Reserved</MenuItem>
              <MenuItem value="maintenance">Maintenance</MenuItem>
              <MenuItem value="out_of_order">Out of Order</MenuItem>
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              Save
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <RoomDetailDialog
        open={detailDialogOpen}
        roomId={selectedRoomId}
        onClose={() => {
          setDetailDialogOpen(false);
          setSelectedRoomId(null);
        }}
        onSuccess={() => {
          fetchRooms();
        }}
      />
    </Box>
  );
};

export default Rooms;

